import React, { useState, useEffect, useMemo, useCallback } from 'react';
import DOMPurify from 'dompurify';
import { Layout, PageHeader } from '../components/Layout';
import { useAuth } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Plus, Download, Printer, List, CalendarDays } from 'lucide-react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isSameMonth, addMonths, subMonths, parseISO, startOfWeek, endOfWeek, addDays, isToday, addWeeks, subWeeks } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const statusColors = {
  brouillon: 'bg-slate-200 border-slate-300 text-slate-700',
  en_attente: 'bg-amber-100 border-amber-300 text-amber-800',
  confirme: 'bg-[#1e3a8a] border-[#1e3a8a] text-white',
  annule: 'bg-red-100 border-red-300 text-red-700',
  termine: 'bg-emerald-100 border-emerald-300 text-emerald-700',
  expire: 'bg-gray-100 border-gray-300 text-gray-600',
};

const statusLabels = {
  brouillon: 'Brouillon',
  en_attente: 'En attente',
  confirme: 'Confirmé',
  annule: 'Annulé',
  termine: 'Terminé',
  expire: 'Expiré',
};

export default function CalendarPage() {
  const { user } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('month');
  const [reservations, setReservations] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const isUser = user?.role === 'utilisateur';

  useEffect(() => {
    loadData();
  }, [currentDate, view]);  // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = useCallback(async () => {
    try {
      const start = view === 'month' 
        ? startOfMonth(currentDate) 
        : startOfWeek(currentDate, { weekStartsOn: 1 });
      const end = view === 'month' 
        ? endOfMonth(currentDate) 
        : endOfWeek(currentDate, { weekStartsOn: 1 });

      const [resResponse, roomsResponse] = await Promise.all([
        api.get('/reservations', {
          params: {
            start_date: start.toISOString(),
            end_date: addDays(end, 1).toISOString()
          }
        }),
        api.get('/rooms')
      ]);
      
      setReservations(resResponse.data);
      setRooms(roomsResponse.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [currentDate, view]);

  const filteredReservations = useMemo(() => {
    let result = reservations;
    if (selectedRoom !== 'all') {
      result = result.filter(r => r.room_id === selectedRoom);
    }
    if (selectedStatus !== 'all') {
      result = result.filter(r => r.status === selectedStatus);
    }
    return result;
  }, [reservations, selectedRoom, selectedStatus]);

  const getReservationsForDay = (date) => {
    return filteredReservations.filter(r => {
      const start = parseISO(r.start_datetime);
      return isSameDay(start, date);
    });
  };

  const navigatePrev = () => {
    if (view === 'month') {
      setCurrentDate(subMonths(currentDate, 1));
    } else {
      setCurrentDate(subWeeks(currentDate, 1));
    }
  };

  const navigateNext = () => {
    if (view === 'month') {
      setCurrentDate(addMonths(currentDate, 1));
    } else {
      setCurrentDate(addWeeks(currentDate, 1));
    }
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Generate calendar days
  const calendarDays = useMemo(() => {
    if (view === 'month') {
      const start = startOfWeek(startOfMonth(currentDate), { weekStartsOn: 1 });
      const end = endOfWeek(endOfMonth(currentDate), { weekStartsOn: 1 });
      return eachDayOfInterval({ start, end });
    } else {
      const start = startOfWeek(currentDate, { weekStartsOn: 1 });
      return eachDayOfInterval({ start, end: addDays(start, 6) });
    }
  }, [currentDate, view]);

  const weekDays = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

  // Generate printable content
  const generatePrintContent = (mode) => {
    const start = view === 'month' 
      ? startOfMonth(currentDate) 
      : startOfWeek(currentDate, { weekStartsOn: 1 });
    const end = view === 'month' 
      ? endOfMonth(currentDate) 
      : endOfWeek(currentDate, { weekStartsOn: 1 });
    
    const periodLabel = view === 'month' 
      ? format(currentDate, 'MMMM yyyy', { locale: fr })
      : `Semaine du ${format(start, 'd MMMM yyyy', { locale: fr })}`;
    
    const roomName = selectedRoom === 'all' 
      ? 'Toutes les salles' 
      : rooms.find(r => r.id === selectedRoom)?.name || '';

    let html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Calendrier - ${periodLabel}</title>
        <style>
          @page { size: A4 landscape; margin: 1cm; }
          body { font-family: Arial, sans-serif; font-size: 10pt; }
          h1 { color: #1e3a8a; font-size: 18pt; margin-bottom: 5px; }
          h2 { color: #666; font-size: 12pt; font-weight: normal; margin-top: 0; }
          .header { border-bottom: 2px solid #1e3a8a; padding-bottom: 10px; margin-bottom: 20px; }
          .logo { font-weight: bold; color: #1e3a8a; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; }
          th { background: #1e3a8a; color: white; padding: 8px; text-align: left; }
          td { padding: 6px 8px; border-bottom: 1px solid #ddd; }
          tr:nth-child(even) { background: #f8f9fa; }
          .status { padding: 2px 8px; border-radius: 4px; font-size: 9pt; }
          .status-confirme { background: #d1fae5; color: #065f46; }
          .status-en_attente { background: #fef3c7; color: #92400e; }
          .status-brouillon { background: #f1f5f9; color: #475569; }
          .status-annule { background: #fee2e2; color: #991b1b; }
          .status-termine { background: #dbeafe; color: #1e40af; }
          .calendar-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 2px; }
          .calendar-day { border: 1px solid #ddd; min-height: 80px; padding: 4px; font-size: 9pt; }
          .calendar-day.other-month { background: #f5f5f5; }
          .calendar-day.today { border: 2px solid #c5a059; }
          .day-number { font-weight: bold; margin-bottom: 4px; }
          .event { background: #1e3a8a; color: white; padding: 2px 4px; margin: 1px 0; border-radius: 2px; font-size: 8pt; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
          .event.en_attente { background: #f59e0b; }
          .event.annule { background: #ef4444; opacity: 0.6; }
          .event.termine { background: #10b981; }
          .weekday-header { background: #1e3a8a; color: white; padding: 8px; text-align: center; font-weight: bold; }
          .print-date { color: #666; font-size: 9pt; margin-top: 20px; }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo">Diocèse de Pamiers - Gestion des Salles</div>
          <h1>Calendrier des réservations</h1>
          <h2>${periodLabel} - ${roomName}</h2>
        </div>
    `;

    if (mode === 'list') {
      // List view
      const sortedReservations = [...filteredReservations].sort((a, b) => 
        new Date(a.start_datetime) - new Date(b.start_datetime)
      );

      html += `
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Horaires</th>
              <th>Salle</th>
              <th>Événement</th>
              <th>Bénéficiaire</th>
              <th>Besoins particuliers</th>
              <th>Services</th>
              <th>Statut</th>
            </tr>
          </thead>
          <tbody>
      `;

      sortedReservations.forEach(res => {
        html += `
          <tr>
            <td>${format(parseISO(res.start_datetime), 'dd/MM/yyyy', { locale: fr })}</td>
            <td>${format(parseISO(res.start_datetime), 'HH:mm')} - ${format(parseISO(res.end_datetime), 'HH:mm')}</td>
            <td>${res.room_name}</td>
            <td>${res.title}</td>
            <td>${res.client_name}</td>
            <td>${res.special_needs || '-'}</td>
            <td>${(res.services || []).map(s => ({bouilloire:'Bouilloire',machine_cafe:'Café',video_projection:'Vidéo',visio_conference:'Visio',parking:'Parking',menage:'Ménage'}[s] || s)).join(', ') || '-'}</td>
            <td><span class="status status-${res.status}">${statusLabels[res.status]}</span></td>
          </tr>
        `;
      });

      html += `</tbody></table>`;
    } else {
      // Calendar view
      html += `<div class="calendar-grid">`;
      
      // Week headers
      weekDays.forEach(day => {
        html += `<div class="weekday-header">${day}</div>`;
      });

      // Days
      calendarDays.forEach(day => {
        const dayReservations = getReservationsForDay(day);
        const isCurrentMonth = isSameMonth(day, currentDate);
        const isTodayDate = isToday(day);

        html += `<div class="calendar-day ${!isCurrentMonth ? 'other-month' : ''} ${isTodayDate ? 'today' : ''}">`;
        html += `<div class="day-number">${format(day, 'd')}</div>`;
        
        dayReservations.slice(0, 4).forEach(res => {
          const displayName = res.display_name || res.client_name;
          html += `<div class="event ${res.status}">${format(parseISO(res.start_datetime), 'HH:mm')}-${format(parseISO(res.end_datetime), 'HH:mm')} ${res.room_name}<br/>${displayName}</div>`;
        });
        
        if (dayReservations.length > 4) {
          html += `<div style="font-size:8pt;color:#666">+${dayReservations.length - 4} autres</div>`;
        }
        
        html += `</div>`;
      });

      html += `</div>`;
    }

    html += `
        <p class="print-date">Imprimé le ${format(new Date(), "d MMMM yyyy 'à' HH:mm", { locale: fr })}</p>
      </body>
      </html>
    `;

    return html;
  };

  const handlePrint = (mode) => {
    const content = generatePrintContent(mode);
    const sanitized = DOMPurify.sanitize(content, { WHOLE_DOCUMENT: true, ADD_TAGS: ['style', 'meta', 'title', 'link'], ADD_ATTR: ['media'] });
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      // document.write inherits the UTF-8 encoding of the parent React app,
      // bypassing the unreliable charset interpretation of blob: URLs.
      printWindow.document.open('text/html', 'replace');
      printWindow.document.write(sanitized);
      printWindow.document.close();
      printWindow.onload = () => {
        printWindow.print();
      };
    }
  };

  const handleDownload = (mode) => {
    const content = generatePrintContent(mode);
    // Encode as UTF-8 bytes with BOM so Windows tools (Notepad, Word, Excel)
    // and browsers reliably detect UTF-8 when the file is opened.
    const bom = new Uint8Array([0xEF, 0xBB, 0xBF]);
    const utf8 = new TextEncoder().encode(content);
    const blob = new Blob([bom, utf8], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `calendrier_${format(currentDate, 'yyyy-MM')}_${mode}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Calendrier téléchargé');
  };

  return (
    <Layout>
      <PageHeader 
        title="Calendrier" 
        description={isUser ? "Vos réservations" : "Vue d'ensemble des réservations"}
        actions={
          <div className="flex gap-2">
            {!isUser && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" data-testid="export-calendar-button">
                    <Download className="mr-2 h-4 w-4" />
                    Exporter
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => handlePrint('list')}>
                    <List className="mr-2 h-4 w-4" />
                    Imprimer en liste
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handlePrint('calendar')}>
                    <CalendarDays className="mr-2 h-4 w-4" />
                    Imprimer en calendrier
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleDownload('list')}>
                    <Download className="mr-2 h-4 w-4" />
                    Télécharger liste (HTML)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleDownload('calendar')}>
                    <Download className="mr-2 h-4 w-4" />
                    Télécharger calendrier (HTML)
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <Button 
              onClick={() => navigate('/reservations?new=true')}
              className="bg-[#1e3a8a] hover:bg-[#1e40af] btn-animate"
              data-testid="calendar-new-reservation-button"
            >
              <Plus className="mr-2 h-4 w-4" />
              Nouvelle réservation
            </Button>
          </div>
        }
      />

      <div className="p-8 space-y-6" data-testid="calendar-content">
        {/* Controls */}
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={navigatePrev}
                  data-testid="calendar-prev"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={navigateNext}
                  data-testid="calendar-next"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  onClick={goToToday}
                  data-testid="calendar-today"
                >
                  Aujourd'hui
                </Button>
                <h2 className="text-xl font-semibold text-[#1e3a8a] ml-4" style={{ fontFamily: 'Merriweather, serif' }}>
                  {format(currentDate, view === 'month' ? 'MMMM yyyy' : "'Semaine du' d MMMM yyyy", { locale: fr })}
                </h2>
              </div>

              <div className="flex items-center gap-3">
                <Select value={selectedRoom} onValueChange={setSelectedRoom}>
                  <SelectTrigger className="w-[180px]" data-testid="calendar-room-filter">
                    <SelectValue placeholder="Toutes les salles" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes les salles</SelectItem>
                    {rooms.map(room => (
                      <SelectItem key={room.id} value={room.id}>{room.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                  <SelectTrigger className="w-[160px]" data-testid="calendar-status-filter">
                    <SelectValue placeholder="Tous les statuts" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les statuts</SelectItem>
                    <SelectItem value="confirme">Confirmé</SelectItem>
                    <SelectItem value="en_attente">En attente</SelectItem>
                    <SelectItem value="brouillon">Brouillon</SelectItem>
                    <SelectItem value="termine">Terminé</SelectItem>
                    <SelectItem value="annule">Annulé</SelectItem>
                  </SelectContent>
                </Select>

                <div className="flex rounded-md border border-slate-200 overflow-hidden">
                  <Button
                    variant={view === 'month' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setView('month')}
                    className={view === 'month' ? 'bg-[#1e3a8a]' : ''}
                    data-testid="view-month"
                  >
                    Mois
                  </Button>
                  <Button
                    variant={view === 'week' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setView('week')}
                    className={view === 'week' ? 'bg-[#1e3a8a]' : ''}
                    data-testid="view-week"
                  >
                    Semaine
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Calendar Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner" style={{ width: 40, height: 40 }} />
          </div>
        ) : (
          <Card className="border-slate-200 overflow-hidden">
            <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
              {weekDays.map(day => (
                <div key={day} className="p-3 text-center text-sm font-semibold text-slate-600 border-r last:border-r-0">
                  {day}
                </div>
              ))}
            </div>

            <div className={`grid grid-cols-7 ${view === 'week' ? 'min-h-[500px]' : ''}`}>
              {calendarDays.map((day, index) => {
                const dayReservations = getReservationsForDay(day);
                const isCurrentMonth = isSameMonth(day, currentDate);
                const isCurrentDay = isToday(day);

                return (
                  <div
                    key={day.toISOString()}
                    className={`
                      ${view === 'month' ? 'min-h-[120px]' : 'min-h-[500px]'}
                      border-r border-b last:border-r-0 p-2
                      ${!isCurrentMonth ? 'bg-slate-50' : 'bg-white'}
                      ${isCurrentDay ? 'ring-2 ring-inset ring-[#c5a059]' : ''}
                    `}
                    data-testid={`calendar-day-${format(day, 'yyyy-MM-dd')}`}
                  >
                    <div className={`
                      text-sm font-medium mb-1
                      ${!isCurrentMonth ? 'text-slate-400' : 'text-slate-700'}
                      ${isCurrentDay ? 'text-[#c5a059]' : ''}
                    `}>
                      {format(day, 'd')}
                    </div>

                    <div className="space-y-1">
                      {dayReservations.slice(0, view === 'month' ? 3 : undefined).map(res => (
                        <Popover key={res.id}>
                          <PopoverTrigger asChild>
                            <button
                              className={`
                                w-full text-left text-xs p-1.5 rounded border
                                hover:opacity-80 transition-opacity
                                ${statusColors[res.status] || 'bg-slate-100 border-slate-300 text-slate-600'}
                              `}
                              data-testid={`calendar-event-${res.id}`}
                            >
                              <div className="font-medium">
                                {format(parseISO(res.start_datetime), 'HH:mm')} - {format(parseISO(res.end_datetime), 'HH:mm')}
                              </div>
                              <div className="truncate">{res.room_name}</div>
                              <div className="truncate opacity-90">{res.display_name || res.client_name}</div>
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="w-72 p-4" align="start">
                            <div className="space-y-2">
                              <h4 className="font-semibold text-[#1e3a8a]">{res.title}</h4>
                              <p className="text-sm text-slate-600"><strong>Salle :</strong> {res.room_name}</p>
                              <p className="text-sm text-slate-600">
                                <strong>Bénéficiaire :</strong> {res.display_name || res.client_name}
                                {res.company_name && res.company_name !== res.client_name && (
                                  <span className="block text-slate-400 text-xs">({res.client_name})</span>
                                )}
                              </p>
                              <p className="text-sm text-slate-500">
                                {format(parseISO(res.start_datetime), "d MMM yyyy 'de' HH:mm", { locale: fr })}
                                {' à '}
                                {format(parseISO(res.end_datetime), 'HH:mm', { locale: fr })}
                              </p>
                              {res.special_needs && (
                                <p className="text-sm text-amber-700 bg-amber-50 p-2 rounded border border-amber-200">
                                  <strong>Besoins :</strong> {res.special_needs}
                                </p>
                              )}
                              {res.services && res.services.length > 0 && (
                                <div className="text-xs text-slate-600 bg-slate-50 p-2 rounded border border-slate-200">
                                  <strong>Services :</strong>{' '}
                                  {res.services.map(s => ({
                                    bouilloire: 'Bouilloire', machine_cafe: 'Machine à café',
                                    video_projection: 'Vidéo-projection', visio_conference: 'Visio-conférence',
                                    parking: 'Parking', menage: 'Ménage'
                                  }[s] || s)).join(', ')}
                                </div>
                              )}
                              <Badge className={(statusColors[res.status] || 'bg-slate-100 border-slate-300 text-slate-600').replace('border-', 'border ')}>
                                {statusLabels[res.status] || res.status}
                              </Badge>
                              <div className="pt-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => navigate('/reservations')}
                                  className="w-full"
                                >
                                  Voir les détails
                                </Button>
                              </div>
                            </div>
                          </PopoverContent>
                        </Popover>
                      ))}
                      {view === 'month' && dayReservations.length > 3 && (
                        <div className="text-xs text-slate-500 font-medium pl-1">
                          +{dayReservations.length - 3} autres
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* Legend */}
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <span className="text-sm font-medium text-slate-600">Légende :</span>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-[#1e3a8a]" />
                <span className="text-sm text-slate-600">Confirmé</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-amber-200 border border-amber-300" />
                <span className="text-sm text-slate-600">En attente</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-slate-200 border border-slate-300" />
                <span className="text-sm text-slate-600">Brouillon</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-emerald-200 border border-emerald-300" />
                <span className="text-sm text-slate-600">Terminé</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-red-200 border border-red-300" />
                <span className="text-sm text-slate-600">Annulé</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
